import { defineStore } from 'pinia';
import { useStoreQuery } from '../client/query';

export enum EOverlayItemType {
  modal = 'modal',
  fullscreen = 'fullscreen',
  popup = 'popup',
  dropdown = 'dropdown',
  other = 'other',
}

export interface IOverlayItem {
  name: string;
  params?: any;
  type: EOverlayItemType;
  // param that should prevent all others layouts to be shown
  shouldOverlap?: boolean;
  // for overlays that should be "main"
  shouldBeAttchedToQuery?: boolean;
}

export interface IOverlayQueueParams {
  force?: boolean;
  forceQuery?: boolean;
}

export const useOverlayQueueStore = defineStore({
  id: 'overlayQueue',
  state: () => ({
    queue: [] as IOverlayItem[],
  }),
  getters: {
    queueMap: (state) =>
      state.queue.reduce((acc, queueItem) => ({ ...acc, [queueItem.name]: queueItem }), {} as any),
    queueList: (state) => state.queue.map((queueItem) => queueItem.name),
  },
  actions: {
    checkIfPresentInOverlayQueue(name: string) {
      return this.queue.some((queueItem) => queueItem.name === name);
    },
    checkIfPresentInOverlayQueueByType(type: EOverlayItemType) {
      return this.queue.some((queueItem) => queueItem.type === type);
    },
    addToOverlayQueue(item: IOverlayItem, { force, forceQuery }: IOverlayQueueParams = {}) {
      const mainOverlayItemByType = this.queue.find(
        (queueItem) => queueItem.shouldBeAttchedToQuery && queueItem.type === item.type
      );
      if (mainOverlayItemByType && item.shouldBeAttchedToQuery) {
        if (force && mainOverlayItemByType) {
          this.removeFromOverlayQueue(mainOverlayItemByType.name);
        } else {
          throw new Error('ERROR: Trying rewrite opened overlay');
        }
      }

      const index = this.queue.findIndex((queueItem) => queueItem.name === item.name);

      if (index >= 0) {
        this.queue.splice(index, 1, item);
      } else {
        this.queue.push(item);
      }

      if (item.shouldBeAttchedToQuery) {
        if (forceQuery) {
          useStoreQuery().setParam({
            [item.type]: item.name,
            ...item.params,
          });
        } else {
          useStoreQuery().updateParam({
            [item.type]: item.name,
            ...item.params,
          });
        }
      }
    },
    removeFromOverlayQueue(name: string) {
      const index = this.queue.findIndex((queueItem) => queueItem.name === name);
      if (index > -1) {
        if (this.queueMap[name].shouldBeAttchedToQuery) {
          useStoreQuery().removeParams({
            ...this.queueMap[name].params,
            [this.queueMap[name].type]: this.queueMap[name].name,
          });
        }
        this.queue.splice(index, 1);
      }
    },
    removeFromOverlayQueueByType(type: EOverlayItemType) {
      const itemsToRemove = this.queue.filter((queueItem) => queueItem.type === type);

      this.queue = this.queue.filter((queueItem) => queueItem.type !== type);

      itemsToRemove.forEach((queueItem) => {
        if (queueItem.shouldBeAttchedToQuery) {
          useStoreQuery().removeParams({
            ...queueItem.params,
            [queueItem.type]: queueItem.name,
          });
        }
      });
    },
    updateOverlayQueueItem(item: IOverlayItem) {
      const index = this.queue.findIndex((queueItem) => queueItem.name === item.name);

      this.queue[index] = { ...item };

      if (item.shouldBeAttchedToQuery) {
        useStoreQuery().updateParam({
          [item.type]: item.name,
          ...item.params,
        });
      }
    },
    replaceOverlayQueueItem(item: IOverlayItem) {
      const foundQueueItem = this.queue.find((queueItem) => item.type === queueItem.type);
      if (foundQueueItem) {
        this.removeFromOverlayQueue(foundQueueItem?.name);
      }

      this.addToOverlayQueue(item);
    },
  },
});
